import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Chip,
  Fade,
  Grow,
} from "@mui/material";
import { ArrowForward as ArrowForwardIcon } from "@mui/icons-material";

// Import images
import snowleopardImage from "../../../images/snowleopard.png";
import leopardImage from "../../../images/leopard.png";
import foxImage from "../../../images/fox.png";
import deerImage from "../../../images/deer.png";
import markhorImage from "../../../images/markhor.png";
import vehiclepersonImage from "../../../images/vehicleperson.png";

export default function SpeciesInfo() {
  const sections = [
    {
      title: "Snow Leopard Detection",
      description:
        "Our AI-powered system automatically detects and classifies snow leopards in real-time. With over 95% accuracy, we help conservationists track and protect this endangered species.",
      image: snowleopardImage,
      imageLeft: true,
      stats: "95% Detection Accuracy",
    },
    {
      title: "Leopard Monitoring",
      description:
        "Monitor leopard populations with our advanced camera trap system. Get instant alerts when leopards are detected and track their movement patterns across different territories.",
      image: leopardImage,
      imageLeft: false,
      stats: "24/7 Live Monitoring",
    },
    {
      title: "Fox Population Tracking",
      description:
        "Track fox populations and their habitat usage with real-time data. Our system helps researchers understand fox behavior and movement patterns.",
      image: foxImage,
      imageLeft: true,
      stats: "156 Foxes Tracked",
    },
    {
      title: "Deer Conservation",
      description:
        "Monitor deer populations and migration patterns across protected areas. Our advanced analytics help conservationists make informed decisions.",
      image: deerImage,
      imageLeft: false,
      stats: "278 Deer Detected",
    },
    {
      title: "Markhor Protection",
      description:
        "Protect the majestic Markhor with our advanced monitoring system. Track population trends and habitat usage to support conservation efforts.",
      image: markhorImage,
      imageLeft: true,
      stats: "89 Markhor Tracked",
    },
    {
      title: "Vehicle & Person Detection",
      description:
        "Monitor human-wildlife interactions and potential threats. Our system detects vehicles and persons to help prevent poaching and human-wildlife conflicts.",
      image: vehiclepersonImage,
      imageLeft: false,
      stats: "Real-time Detection",
    },
  ];

  return (
    <>
      {sections.map((section, index) => (
        <Box
          key={index}
          sx={{
            py: 8,
            background:
              index % 2 === 0
                ? "linear-gradient(180deg, #484848 0%, #1a1a1a 100%)"
                : "linear-gradient(180deg, #1a1a1a 0%, #0d0d0d 100%)",
            position: "relative",
            overflow: "hidden",
          }}
          id={index === 0 ? "species-info" : undefined}
        >
          <Container maxWidth="xl">
            <Grid container spacing={6} alignItems="center">
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  order: section.imageLeft ? 1 : 2,
                }}
              >
                <Fade in timeout={1000}>
                  <Box>
                    <Chip
                      label={section.stats}
                      sx={{
                        mb: 2,
                        bgcolor: "rgba(255,255,255,0.08)",
                        color: "#4CAF50",
                        borderRadius: 2,
                        px: 2,
                        py: 1,
                        border: "1px solid rgba(76,175,80,0.3)",
                      }}
                    />
                    <Typography
                      variant="h4"
                      fontWeight={700}
                      gutterBottom
                      sx={{ color: "#fff" }}
                    >
                      {section.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "rgba(255,255,255,0.7)",
                        mb: 3,
                        lineHeight: 1.8,
                      }}
                    >
                      {section.description}
                    </Typography>
                    <Button
                      variant="contained"
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        background:
                          "linear-gradient(135deg, #454545 0%, rgba(129, 129, 129, 0.95) 100%)",
                        px: 4,
                        py: 1.5,
                        borderRadius: 3,
                        "&:hover": {
                          transform: "translateX(8px)",
                          boxShadow: "0 8px 30px rgba(76,175,80,0.4)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      Learn More
                    </Button>
                  </Box>
                </Fade>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  order: section.imageLeft ? 2 : 1,
                }}
              >
                <Grow in timeout={1000}>
                  <Box
                    sx={{
                      position: "relative",
                      borderRadius: 4,
                      overflow: "hidden",
                      boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
                      aspectRatio: "4/3",
                      background:
                        "linear-gradient(135deg, rgba(26,26,26,0.8) 0%, rgba(45,45,45,0.8) 100%)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      component="img"
                      src={section.image}
                      alt={section.title}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        transition: "transform 0.6s ease",
                        "&:hover": {
                          transform: "scale(1.05)",
                        },
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 20,
                        left: 20,
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        background: "rgba(0,0,0,0.6)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255,255,255,0.1)",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "rgba(255,255,255,0.8)" }}
                      >
                        {section.title}
                      </Typography>
                    </Box>
                  </Box>
                </Grow>
              </Grid>
            </Grid>
          </Container>
        </Box>
      ))}
    </>
  );
}
